import ApiService from '../apiService';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import { trackEvent } from '@/utils/tracking';

export default {
  // **********************************************************
  //
  // DIGITAL TWIN
  //
  // **********************************************************
  digitalTwinsFetch(
    context,
    userId,
    scopeIds,
    filters,
    options,
    orderBy,
    offset,
    limit,
    searchItems,
    includeIdentifiers,
    includeProperties,
    includeReferences
  ) {
    let cleanedScopeIds = scopeIds;
    if (cleanedScopeIds) {
      cleanedScopeIds.sort();
      cleanedScopeIds = cleanedScopeIds.map((sId) => parseInt(sId));
    }
    let scopeIdsJson = JSON.stringify(cleanedScopeIds);
    if (!scopeIdsJson) {
      scopeIdsJson = 'null';
    }
    let optionsJson = JSON.stringify(options);
    if (!optionsJson) {
      optionsJson = 'null';
    }
    let filtersJson = JSON.stringify(filters);
    if (!filtersJson) {
      filtersJson = 'null';
    }
    let searchJson = JSON.stringify(searchItems);
    if (!searchJson) {
      searchJson = 'null';
    }
    let orderByJson = JSON.stringify(orderBy);
    if (!orderByJson) {
      orderByJson = 'null';
    }
    let params = {
      user_id: userId,
      scope_ids: scopeIdsJson,
      offset,
      limit,
      search_items: searchJson,
      filters: filtersJson,
      order_by: orderByJson,
      options: optionsJson,
      include_identifiers: includeIdentifiers,
      include_properties: includeProperties,
      include_references: includeReferences,
    };

    return ApiService.instance.get('/digitaltwin', { params }).then((response) => {
      return response.data;
    });
  },

  digitalTwinFetchTagsAndZones() {
    return ApiService.instance.get('/digitaltwin/tags-zones').then((response) => {
      return response.data;
    });
  },

  digitalTwinFetchChildren(
    context,
    parentId,
    offset,
    limit,
    userId = undefined,
    options = ['bare'],
    force = false,
    tenantId = undefined
  ) {
    let optionsJson = JSON.stringify(options);
    if (!optionsJson) {
      optionsJson = 'null';
    }
    let params = {
      offset,
      limit: limit <= 0 ? undefined : limit,
      user_id: userId,
      tenant_id: tenantId,
      options: optionsJson,
    };

    const cacheKey = `children.parent:${parentId}.user:${userId}.tenantId:${tenantId}.offset:${offset}.limit:${limit}.options:${JSON.stringify(
      optionsJson
    )}`;
    if (!force && context.$store.state.app.dtwins) {
      if (context.$store.state.app.dtwins[cacheKey]) {
        return Promise.resolve(context.$store.state.app.dtwins[cacheKey]);
      }
    }

    const parentIdPath = parentId ? parentId : '';

    return ApiService.instance.get(`/digitaltwin/children/${parentIdPath}`, { params }).then((response) => {
      context.$store.state.app.dtwins[cacheKey] = response.data;
      return response.data;
    });
  },

  digitalTwinFetchPartialTree(context, dtwinId, searchItem, parentId, options = ['bare'], tenantId = undefined) {
    let optionsJson = JSON.stringify(options);
    if (!optionsJson) {
      optionsJson = 'null';
    }
    let params = {
      dtwin_id: dtwinId,
      search_item: searchItem,
      parent_id: parentId,
      tenant_id: tenantId,
      options: optionsJson,
    };
    return ApiService.instance.get('/digitaltwin/partial-tree/', { params }).then((response) => {
      return response.data;
    });
  },

  digitalTwinFetchGeoMetaInfos(context, dtwinId, searchItems) {
    let searchJson = JSON.stringify(searchItems);
    if (!searchJson) {
      searchJson = 'null';
    }
    return ApiService.instance
      .get('/digitaltwin/geo-meta-infos/', {
        params: { twin_id: dtwinId, search_items: searchJson },
      })
      .then((response) => {
        return response.data;
      });
  },

  digitalTwinFetchGeoClusters(context, dtwinId, floorDtwinId, geoBounds, stateKey, searchItems, parameters) {
    let geoBoundsJson = JSON.stringify(geoBounds);
    if (!geoBoundsJson) {
      geoBoundsJson = 'null';
    }
    let searchJson = JSON.stringify(searchItems);
    if (!searchJson) {
      searchJson = 'null';
    }
    if (parameters.excludeZones === undefined) {
      parameters.excludeZones = false;
    }
    if (parameters.clusterDistance === undefined) {
      parameters.clusterDistance = 50;
    }
    if (parameters.hideStatelessPolygons === undefined) {
      parameters.hideStatelessPolygons = false;
    }
    let parametersJson = JSON.stringify(parameters);

    return ApiService.instance
      .get('/digitaltwin/geo-clusters/', {
        params: {
          twin_id: dtwinId,
          floor_dtwin_id: floorDtwinId,
          geo_bounds: geoBoundsJson,
          state_key: stateKey,
          search_items: searchJson,
          parameters: parametersJson,
        },
        timeout: 25000,
      })
      .then((response) => {
        return response.data;
      });
  },

  digitalTwinGetGeoClusterContent(
    context,
    clusterId,
    dtwinId,
    floorDtwinId,
    geoBounds,
    searchItems,
    excludeZones = false,
    clusterDistance = 50
  ) {
    let geoBoundsJson = JSON.stringify(geoBounds);
    if (!geoBoundsJson) {
      geoBoundsJson = 'null';
    }
    let searchJson = JSON.stringify(searchItems);
    if (!searchJson) {
      searchJson = 'null';
    }
    return ApiService.instance
      .get(`/digitaltwin/geo-clusters/break-open/${clusterId}`, {
        params: {
          twin_id: dtwinId,
          floor_dtwin_id: floorDtwinId,
          geo_bounds: geoBoundsJson,
          exclude_zones: excludeZones,
          search_items: searchJson,
          cluster_distance: clusterDistance,
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  digitalTwinFetchGeoHistory(id, dateStart, dateEnd, offset, limit) {
    return ApiService.instance
      .get(`/digitaltwin/${id}/geo-history`, { params: { dateStart, dateEnd, offset, limit } })
      .then((response) => {
        return response.data;
      });
  },

  digitalTwinGet(context, id, bare) {
    return ApiService.instance
      .get(`/digitaltwin/${id}`, { params: { bare: bare ? true : false, no_descendents: true } })
      .then((response) => {
        return response.data;
      });
  },

  async digitalTwinCreate(context, data) {
    const response = await ApiService.instance.post('/digitaltwin', data);
    trackEvent('digitaltwins', 'createOne');
    return response.data;
  },

  async digitalTwinUpdate(context, data) {
    const response = await ApiService.instance.put(`/digitaltwin/${data.id}`, data);
    trackEvent('digitaltwins', 'updateOne');
    return response.data;
  },

  async digitalTwinUpdateDisabled(context, id, disabled) {
    const response = await ApiService.instance.post(`/digitaltwin/${id}/disabled/${disabled}`);
    trackEvent('digitaltwins', disabled ? 'disableOne' : 'enableOne');
    return response.data;
  },

  async digitalTwinCreateScope(context, id) {
    const response = await ApiService.instance.post(`/digitaltwin/${id}/create-scope`);
    trackEvent('digitaltwins', 'createScope');
    return response.data;
  },

  async digitalTwinDelete(id) {
    const response = await ApiService.instance.delete(`/digitaltwin/${id}`);
    trackEvent('digitaltwins', 'deleteOne');
    return response.data;
  },

  digitalTwinGetDependencies(id) {
    return ApiService.instance.get(`/digitaltwin/${id}/dependencies`).then((response) => {
      return response.data;
    });
  },

  async digitalTwinDuplicate(id, twinsData) {
    const response = await ApiService.instance.post('/background-runners/digitaltwin-bulk-duplicate/', {
      id,
      twinsData,
    });
    trackEvent('digitaltwins', 'duplicate');
    return response.data;
  },

  async digitalTwinSync(id, syncData) {
    const response = await ApiService.instance.post(`/digitaltwin/${id}/synchronize/`, syncData);
    trackEvent('digitaltwins', 'syncOne');
    return response.data;
  },

  // **********************************************************
  //
  // DTWIN SHARING
  //
  // **********************************************************

  digitalTwinCanShare(context, twinId) {
    return ApiService.instance.get(`/digitaltwin/sharing/can-share/${twinId}`).then((response) => {
      return response.data;
    });
  },

  // similar endpoint to `digitalTwinCanShare`, but just checks if the sharing-module is enabled at all,
  // while only requiring `DigitalTwin.read` permissions. `can-share` also checks for manage permission,
  // i.e. can the CURRENT user share a twin, rather than IF the twin is shared
  digitalTwinIsShared(context, twinId) {
    return ApiService.instance.get(`/digitaltwin/sharing/is-shared/${twinId}`).then((response) => {
      return response.data;
    });
  },

  digitalTwinFindExistingShareKey(context, twinId) {
    return ApiService.instance.get(`/digitaltwin/sharing/find-existing-key/${twinId}`).then((response) => {
      return response.data;
    });
  },

  async digitalTwinCreateShareKey(context, twinId) {
    const response = await ApiService.instance.post(`/digitaltwin/sharing/create-key/${twinId}`);
    trackEvent('digitaltwins', 'createShareKey');
    return response.data;
  },

  async digitalTwinDeleteShareKey(context, twinId) {
    const response = await ApiService.instance.delete(`/digitaltwin/sharing/delete-key/${twinId}`);
    trackEvent('digitaltwins', 'deleteShareKey');
    return response.data;
  },

  // **********************************************************
  //
  // DTWIN STATES
  //
  // **********************************************************

  // This is the old API -> influxStatesInfluxSearch is used
  digitalTwinStatesSearch(context, dtwinIds, bindingIds, queryItems, offset, limit, order) {
    return ApiService.instance
      .get('/digitaltwin/states/history/search', {
        params: {
          dtwin_ids: JSON.stringify(dtwinIds),
          binding_ids: JSON.stringify(bindingIds),
          query_items: JSON.stringify(queryItems),
          offset,
          limit: limit <= 0 ? undefined : limit,
          order: JSON.stringify(order),
        },
        timeout: 30000,
      })
      .then((response) => {
        return response.data;
      });
  },

  // new API targeting the new table, using IDs from the influx-history-metadata
  digitalTwinStatesHistoryFetchByUuid(context, postgresIds) {
    return ApiService.instance
      .get('/digitaltwin/states/history/postgres-fetch', {
        params: {
          postgres_ids: JSON.stringify(postgresIds),
        },
        timeout: 30000,
      })
      .then((response) => {
        return response.data;
      });
  },

  influxStatesInfluxSearch(context, dtwinIds, bindingIds, queryItems, offset, limit, order) {
    return ApiService.instance
      .get('/digitaltwin/states/history/influx/search', {
        params: {
          dtwin_ids: JSON.stringify(dtwinIds),
          binding_ids: JSON.stringify(bindingIds),
          query_items: JSON.stringify(queryItems),
          offset,
          limit: limit <= 0 ? undefined : limit,
          order: JSON.stringify(order),
        },
        timeout: 25000,
      })
      .then((response) => {
        return response.data;
      });
  },

  influxStatesSearchAggregated(
    context,
    dtwinIds,
    bindingIds,
    queryItems,
    startDate,
    endDate,
    method = 'avg',
    extraParams = {}
  ) {
    return ApiService.instance
      .get('/digitaltwin/states/history/influx/search-aggregated', {
        params: {
          dtwin_ids: JSON.stringify(dtwinIds),
          binding_ids: JSON.stringify(bindingIds),
          query_items: JSON.stringify(queryItems),
          startDate,
          endDate,
          method,
          duration: get(extraParams, 'interval'),
          min: get(extraParams, 'min', false),
          max: get(extraParams, 'max', false),
          latest: get(extraParams, 'latest', false),
          rawData: get(extraParams, 'rawData', false),
        },
        timeout: 25000,
      })
      .then((response) => {
        return response.data;
      });
  },

  digitalTwinFetchStates(context, dtwinId, requestParameters) {
    return ApiService.instance
      .get(`/digitaltwin/states/dtwin/${dtwinId}/`, {
        params: {
          requestParameters: JSON.stringify(requestParameters),
        },
      })
      .then((response) => {
        return sortBy(response.data, ['properties.orderIdx', 'createdAt']);
      });
  },

  digitalTwinsFetchStates(context, dtwinIds, requestParameters) {
    return ApiService.instance
      .get('/digitaltwin/states/dtwins', {
        params: {
          dtwinIds: JSON.stringify(dtwinIds),
          requestParameters: JSON.stringify(requestParameters),
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  digitalTwinGetState(context, stateId) {
    return ApiService.instance.get(`/digitaltwin/states/${stateId}`).then((response) => {
      return response.data;
    });
  },

  async digitalTwinCreateState(context, data) {
    const response = await ApiService.instance.post(`/digitaltwin/states/dtwin/${data.dtwinId}/state/`, data);
    trackEvent('digitaltwins', 'createState');
    return response.data;
  },

  async digitalTwinUpdateState(context, data) {
    const response = await ApiService.instance.put(`/digitaltwin/states/dtwin/${data.dtwinId}/state/${data.id}`, data);
    trackEvent('digitaltwins', 'updateState');
    return response.data;
  },

  async digitalTwinUpdateStateValue(context, data) {
    const response = await ApiService.instance.put(
      `/digitaltwin/states/dtwin/${data.dtwinId}/state/${data.id}/value`,
      data
    );
    trackEvent('digitaltwins', 'updateStateValue');
    return response;
  },

  async digitalTwinUpdateStatesOrder(context, dtwinId, data) {
    const response = await ApiService.instance.post(`/digitaltwin/states/dtwin/${dtwinId}/order`, data);
    trackEvent('digitaltwins', 'updateStatesOrder');
    return response;
  },

  async digitalTwinDeleteState(context, dtwinId, id) {
    const response = await ApiService.instance.delete(`/digitaltwin/states/dtwin/${dtwinId}/state/${id}`);
    trackEvent('digitaltwins', 'deleteState');
    return response;
  },

  digitalTwinGetStateLinkedAggregations(dtwinId, id) {
    return ApiService.instance
      .get(`/digitaltwin/states/dtwin/${dtwinId}/state/${id}/linked-aggregations`)
      .then((response) => {
        return response.data;
      });
  },

  digitalTwinStatesGetAggregateValue(context, dtwinId, data) {
    return ApiService.instance.post(`/digitaltwin/states/dtwin/${dtwinId}/aggregate/value`, data).then((response) => {
      return response.data;
    });
  },

  digitalTwinStateCheckForAggregateLoop(context, dtwinId, stateId, data) {
    return ApiService.instance
      .post(`/digitaltwin/states/dtwin/${dtwinId}/state/${stateId}/checkForAggregateLoop`, data)
      .then((response) => {
        return response.data;
      });
  },

  // **********************************************************
  //
  // DTWIN RULES
  //
  // **********************************************************
  digitalTwinFetchRules(context, dtwinId) {
    return ApiService.instance.get(`/digitaltwin/rules/dtwin/${dtwinId}/`).then((response) => {
      return response.data;
    });
  },

  digitalTwinFetchRulesOverviewList(context, dtwinId, offset, limit, order, searchItem, includeVDRules) {
    return ApiService.instance
      .get(`/digitaltwin/rules/dtwin/${dtwinId}/overview-list`, {
        params: {
          offset,
          limit,
          order: JSON.stringify(order),
          search_item: searchItem,
          include_vd_rules: includeVDRules,
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  digitalTwinFetchRulesExecutionsOverviewList(context, dtwinId, ruleId) {
    return ApiService.instance
      .get(`/digitaltwin/rules/dtwin/${dtwinId}/overview-list/${ruleId}/executions`)
      .then((response) => {
        return response.data;
      });
  },

  digitalTwinFetchRulesWithErrornousExecutions(
    context,
    offset,
    limit,
    order,
    searchItems,
    filters,
    options,
    includeVDRules
  ) {
    return ApiService.instance
      .get('/digitaltwin/rules/errornous-executions', {
        params: {
          offset,
          limit,
          order: JSON.stringify(order),
          search_items: searchItems,
          include_vd_rules: includeVDRules,
          filters: JSON.stringify(filters),
          options: JSON.stringify(options),
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  async digitalTwinCreateRule(context, data) {
    const response = await ApiService.instance.post(`/digitaltwin/rules/dtwin/${data.dtwinId}/rule/`, data);
    trackEvent('digitaltwins', 'createRule');
    return response.data;
  },

  async digitalTwinUpdateRule(context, data) {
    const response = await ApiService.instance.put(`/digitaltwin/rules/dtwin/${data.dtwinId}/rule/${data.id}`, data);
    trackEvent('digitaltwins', 'updateRule');
    return response.data;
  },

  async digitalTwinDeleteRule(context, dtwinId, ruleId) {
    const response = await ApiService.instance.delete(`/digitaltwin/rules/dtwin/${dtwinId}/rule/${ruleId}`);
    trackEvent('digitaltwins', 'deleteRule');
    return response;
  },

  async digitalTwinRuleConvertConditions(context, data) {
    const response = await ApiService.instance.post('/digitaltwin/rules/convert-conditions/', data);
    trackEvent('digitaltwins', 'convertConditions');
    return response.data;
  },

  digitalTwinRuleTest(context, data) {
    return ApiService.instance.post('/digitaltwin/rules/test-conditions/', data).then((response) => {
      return response.data;
    });
  },

  digitalTwinRuleExecuteAction(context, data) {
    return ApiService.instance
      .post('/digitaltwin/rules/test-execute-action/', data, { timeout: 15000 })
      .then((response) => {
        return response.data;
      });
  },

  digitalTwinFetchRuleExecutionHistory(
    context,
    dtwinId,
    ruleId,
    queryStart,
    queryEnd,
    requestParameters,
    offset,
    limit
  ) {
    return ApiService.instance
      .post(`/digitaltwin/rules/execution-history/dtwin/${dtwinId}/rule/${ruleId}`, {
        queryStart,
        queryEnd,
        offset,
        limit,
        requestParameters,
      })
      .then((response) => {
        return response.data;
      });
  },
  fetchDdrSettings(data) {
    return ApiService.instance.get('/digitaltwin/ddr/list', { params: data }).then((response) => {
      return response.data;
    });
  },

  digitalTwinTemplateGetById(id) {
    return ApiService.instanceApiGateway.get(`/digitaltwin-templates/${id}`).then((response) => {
      return response.data;
    });
  },
};
